import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/auth";
import { useToastNavigate } from "@/utils/toastNavigate";
import { getTodayStatus } from "@/Api/timeEntries";

const POLL_INTERVAL_MS = 60000;
// Minutos restantes até o limite de hora extra em que um aviso deve ser disparado
// (do mais distante pro mais próximo do limite).
const OVERTIME_ALERT_THRESHOLDS = [15, 10, 5, 3, 1];

export const usePontoGate = () => {
  const { user } = useAuth();
  const { toast } = useToastNavigate();
  const hasEmployee = !!user?.employee;

  const [loading, setLoading] = useState(hasEmployee);
  const [hasClockedInToday, setHasClockedInToday] = useState(!hasEmployee);
  const [isOverOvertimeLimit, setIsOverOvertimeLimit] = useState(false);
  const [onApprovedAbsenceToday, setOnApprovedAbsenceToday] = useState<{
    absence_type_name: string;
  } | null>(null);
  const [outsideWorkScheduleWindow, setOutsideWorkScheduleWindow] = useState(false);

  const alertedThresholdsRef = useRef<Set<number>>(new Set());

  const fetchStatus = async () => {
    const { data } = await getTodayStatus();
    const response = data?.response;

    setHasClockedInToday(!!response?.has_clocked_in_today);
    setIsOverOvertimeLimit(!!response?.overtime?.is_over_limit);
    setOnApprovedAbsenceToday(response?.on_approved_absence_today ?? null);
    setOutsideWorkScheduleWindow(!!response?.outside_work_schedule_window);

    const minutesRemaining = response?.overtime?.minutes_remaining;
    if (typeof minutesRemaining === "number" && minutesRemaining > 0) {
      const crossed = OVERTIME_ALERT_THRESHOLDS.filter(
        (threshold) =>
          minutesRemaining <= threshold &&
          !alertedThresholdsRef.current.has(threshold)
      );
      if (crossed.length > 0) {
        crossed.forEach((threshold) => alertedThresholdsRef.current.add(threshold));
        toast({
          description: `Atenção: faltam ${minutesRemaining} minuto(s) para atingir o limite diário de hora extra.`,
          action: <AlertTriangle color="#f59e0b" />,
        });
      }
    } else {
      // Sem sessão aberta / sem colaborador em hora extra — libera os avisos pro
      // próximo ciclo (ex: depois de bater Saída e iniciar um novo dia).
      alertedThresholdsRef.current.clear();
    }
  };

  useEffect(() => {
    if (!hasEmployee) {
      setLoading(false);
      return;
    }

    fetchStatus().finally(() => setLoading(false));
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    // Bloqueia tanto quando ainda não bateu Entrada hoje quanto quando estourou o
    // limite de hora extra (nesse segundo caso, a tela forçada bate Saída, já que
    // o backend passa a calcular esse tipo automaticamente — ver determineNextType).
    // Um abono dia-inteiro aprovado cobrindo hoje (férias, licença etc.) libera o
    // gate mesmo sem batida — ver `todayStatus` / `on_approved_absence_today`.
    // Fora da janela do horário de trabalho cadastrado (dia de folga ou fora do
    // expediente), também não força a Entrada — só passa a cobrar quando o
    // colaborador entra dentro do horário esperado.
    hasClockedInToday:
      (hasClockedInToday || !!onApprovedAbsenceToday || outsideWorkScheduleWindow) &&
      !isOverOvertimeLimit,
    isOverOvertimeLimit,
    onApprovedAbsenceToday,
    outsideWorkScheduleWindow,
    markAsClockedIn: () => fetchStatus(),
  };
};
